import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Radio } from "lucide-react";
import {
  snapPosition,
  type PlayoutGeometry,
  type PlayoutType,
  type PlayoutWithAsset,
} from "@production-hub/shared";
import { PlayoutGeometryEditor } from "./PlayoutGeometryEditor";
import {
  usePlayouts,
  usePlayoutAssets,
  useCreatePlayout,
  useUpdatePlayout,
  useDeletePlayout,
  usePreviewPlayout,
  usePlayoutConfig,
} from "@/hooks/useBridgePlayout";

interface PlayoutListProps {
  bridgeUrl: string | undefined | null;
}

/** "Cancom.mp4" -> "Cancom" */
function stripExtension(name?: string): string {
  return name ? name.replace(/\.[^./\\]+$/, "") : "";
}

const DEFAULT_SIZE = 0.75;
/** One second of squeeze at 25fps. */
const DEFAULT_FRAMES = 25;

export function PlayoutList({ bridgeUrl }: PlayoutListProps) {
  const { data: playouts = [] } = usePlayouts(bridgeUrl);
  const { data: assets = [] } = usePlayoutAssets(bridgeUrl);
  const { data: configData } = usePlayoutConfig(bridgeUrl);
  const createPlayout = useCreatePlayout(bridgeUrl);
  const updatePlayout = useUpdatePlayout(bridgeUrl);
  const deletePlayout = useDeletePlayout(bridgeUrl);
  const preview = usePreviewPlayout(bridgeUrl);

  const [editing, setEditing] = useState<PlayoutWithAsset | null>(null);
  const [draft, setDraft] = useState<{
    name: string;
    asset_id: string;
    geometry: PlayoutGeometry;
    in_duration_frames: number;
    out_duration_frames: number;
  } | null>(null);

  const videoMode = configData?.video_mode;

  const startNew = () => {
    setEditing(null);
    setDraft({
      name: "",
      // Nothing preselected: choosing the media is the first decision, and it
      // determines both the type and the default name.
      asset_id: "",
      geometry: {
        ...snapPosition("top-left", DEFAULT_SIZE),
        size: DEFAULT_SIZE,
      },
      in_duration_frames: DEFAULT_FRAMES,
      out_duration_frames: DEFAULT_FRAMES,
    });
  };

  const startEdit = (playout: PlayoutWithAsset) => {
    setEditing(playout);
    setDraft({
      name: playout.name,
      asset_id: playout.asset_id,
      geometry: { x: playout.x, y: playout.y, size: playout.size },
      in_duration_frames: playout.in_duration_frames,
      out_duration_frames: playout.out_duration_frames,
    });
  };

  const save = async () => {
    if (!draft) return;
    const payload = {
      name: draft.name,
      playout_type: derivedType,
      asset_id: draft.asset_id,
      x: draft.geometry.x,
      y: draft.geometry.y,
      size: draft.geometry.size,
      in_duration_frames: draft.in_duration_frames,
      out_duration_frames: draft.out_duration_frames,
    };

    if (editing) {
      await updatePlayout.mutateAsync({ id: editing.id, data: payload });
    } else {
      await createPlayout.mutateAsync(payload);
    }
    setDraft(null);
    setEditing(null);
  };

  const selectedAsset = assets.find((asset) => asset.id === draft?.asset_id);

  // Derived, never stored: a video is played by vMix, a still comes from the
  // ATEM media pool. Keeping it in form state let the two drift apart - a new
  // playout defaulted to "l-shape" while defaulting the asset to a video, and
  // the server rightly rejected the mismatch.
  const derivedType: PlayoutType =
    selectedAsset?.media_type === "video" ? "video-cutin" : "l-shape";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Playouts</CardTitle>
            <CardDescription>
              An L-shape: artwork plus where the program window sits in it.
            </CardDescription>
          </div>
          <Button size="sm" onClick={startNew} disabled={assets.length === 0}>
            <Plus className="mr-1 h-3 w-3" />
            New playout
          </Button>
        </CardHeader>
        <CardContent>
          {playouts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {assets.length === 0
                ? "Upload artwork first."
                : "No playouts yet."}
            </p>
          ) : (
            <div className="space-y-2">
              {playouts.map((playout) => (
                <div
                  key={playout.id}
                  className="flex items-center gap-3 rounded-md border p-2"
                  data-testid="playout-row"
                >
                  {playout.asset &&
                    (playout.asset.media_type === "video" ? (
                      <video
                        src={playout.asset.image_url}
                        className="h-10 w-16 rounded bg-muted object-contain"
                        muted
                        playsInline
                      />
                    ) : (
                      <img
                        src={playout.asset.image_url}
                        alt=""
                        className="h-10 w-16 rounded object-cover"
                      />
                    ))}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{playout.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {Math.round(playout.size * 100)}% window ·{" "}
                      {playout.in_duration_frames}f in /{" "}
                      {playout.out_duration_frames}f out
                    </div>
                  </div>
                  {playout.playout_type === "video-cutin" ? (
                    <Badge
                      variant={
                        playout.asset?.vmix_input === undefined
                          ? "outline"
                          : "secondary"
                      }
                    >
                      {playout.asset?.vmix_input === undefined
                        ? "No vMix input set"
                        : `vMix in ${playout.asset.vmix_input}`}
                    </Badge>
                  ) : (
                    playout.asset?.media_slot === undefined && (
                      <Badge variant="outline">Artwork not synced</Badge>
                    )
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Take to air now"
                    onClick={() => preview.mutate({ id: playout.id })}
                  >
                    <Radio className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startEdit(playout)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deletePlayout.mutate(playout.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {draft && (
        <Card>
          <CardHeader>
            <CardTitle>{editing ? "Edit playout" : "New playout"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>
                  {derivedType === "video-cutin" ? "Preview video" : "Artwork"}
                </Label>
                <Select
                  value={draft.asset_id}
                  onValueChange={(value) => {
                    const chosen = assets.find((a) => a.id === value);
                    // Fill the name from the file, but never overwrite
                    // something the operator typed themselves.
                    const untouched =
                      !draft.name ||
                      draft.name === stripExtension(selectedAsset?.name);
                    setDraft({
                      ...draft,
                      asset_id: value,
                      name: untouched
                        ? stripExtension(chosen?.name)
                        : draft.name,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select artwork" />
                  </SelectTrigger>
                  <SelectContent>
                    {assets.map((asset) => (
                      <SelectItem key={asset.id} value={asset.id}>
                        {asset.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={draft.name}
                  onChange={(event) =>
                    setDraft({ ...draft, name: event.target.value })
                  }
                  placeholder="Sponsor L-bar"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                {/* Derived from the media, not chosen: a video is played out
                    by vMix, a still comes from the ATEM media pool. Letting
                    these disagree produced a playout that pointed at an empty
                    media player instead of the vMix input. */}
                <div
                  className="flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm"
                  data-testid="playout-type"
                >
                  {derivedType === "video-cutin"
                    ? "Video cut-in (played by vMix)"
                    : "L-shape (still from media pool)"}
                </div>
                <p className="text-xs text-muted-foreground">
                  Set by the media you choose below.
                </p>
              </div>
              {derivedType === "video-cutin" && (
                <div className="space-y-2">
                  <Label>vMix input</Label>
                  <div className="flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm">
                    {selectedAsset?.vmix_input !== undefined
                      ? `Input ${selectedAsset.vmix_input}`
                      : "Not set"}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Set on the video itself, under Artwork.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Squeeze in (frames)</Label>
                <Input
                  type="number"
                  min={0}
                  max={300}
                  value={draft.in_duration_frames}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      in_duration_frames: Number(event.target.value),
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Squeeze out (frames)</Label>
                <Input
                  type="number"
                  min={0}
                  max={300}
                  value={draft.out_duration_frames}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      out_duration_frames: Number(event.target.value),
                    })
                  }
                />
              </div>
            </div>

            <PlayoutGeometryEditor
              imageUrl={selectedAsset?.image_url}
              mediaType={selectedAsset?.media_type}
              mediaWidth={selectedAsset?.width}
              mediaHeight={selectedAsset?.height}
              geometry={draft.geometry}
              onChange={(geometry) => setDraft({ ...draft, geometry })}
              frameWidth={videoMode?.width ?? 1920}
              frameHeight={videoMode?.height ?? 1080}
              inDurationFrames={draft.in_duration_frames}
            />

            <div className="flex gap-2">
              <Button
                onClick={save}
                disabled={
                  !draft.name ||
                  !draft.asset_id ||
                  (derivedType === "video-cutin" &&
                    selectedAsset?.vmix_input === undefined)
                }
              >
                {editing ? "Save" : "Create"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setDraft(null);
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
